import { useCallback, useRef, useState, type MouseEvent, type MouseEvent as ReactMouseEvent } from 'react';

/**
 * Selection of rows with checkboxes. A click with Shift selects (or clears) the whole range between the
 * last clicked row and this one, as in file managers and mail programs. `order` is the visible order.
 */
export function useRangeSelection(order: string[]) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const anchor = useRef<string | null>(null);

  const toggle = useCallback(
    (id: string, on: boolean, e?: MouseEvent | { shiftKey?: boolean }) => {
      // read the anchor now: the state updater runs later, after the anchor has moved to this row
      const last = anchor.current;
      setSelected((prev) => {
        const next = new Set(prev);
        const from = last ? order.indexOf(last) : -1;
        const to = order.indexOf(id);
        if (e?.shiftKey && from >= 0 && to >= 0) {
          const [a, b] = from < to ? [from, to] : [to, from];
          for (const x of order.slice(a, b + 1)) {
            if (on) next.add(x);
            else next.delete(x);
          }
        } else if (on) next.add(id);
        else next.delete(id);
        return next;
      });
      anchor.current = id;
    },
    [order],
  );

  const setMany = useCallback((ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const x of ids) {
        if (on) next.add(x);
        else next.delete(x);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelected(new Set());
    anchor.current = null;
  }, []);

  const all = order.length > 0 && order.every((x) => selected.has(x));
  const toggleAll = useCallback(() => setSelected(all ? new Set() : new Set(order)), [all, order]);

  return { selected, toggle, setMany, clear, all, toggleAll, count: selected.size };
}

/**
 * Props for the checkbox of one row. The toggle runs on click, where the Shift key is known (the change
 * event of a checkbox does not carry it reliably); Space on a focused checkbox also fires a click.
 */
export function rowCheck(sel: { selected: Set<string>; toggle: (id: string, on: boolean, e?: { shiftKey?: boolean }) => void }, id: string) {
  return {
    checked: sel.selected.has(id),
    onChange: () => undefined,
    onClick: (e: ReactMouseEvent<HTMLInputElement>) => sel.toggle(id, e.currentTarget.checked, { shiftKey: e.shiftKey }),
  };
}
